import { useGraphQLMutation } from '@/graphql/adapters/use-mutation';
import { GeneratePresignedUrlsMutationDocument } from '@/graphql/documents/generatePresignedUrls';
import { useMutation } from '@tanstack/react-query';

export const useFileUpload = () => {
  const {
    mutateAsync: generatePresignedUrlsMutate,
    isLoading: isGeneratingPresignedUrls,
  } = useGraphQLMutation(GeneratePresignedUrlsMutationDocument, {
    mutationKey: ['generatePresignedUrls'],
  });

  const {
    mutateAsync: uploadFileUsingPresignedUrlMutate,
    isLoading: isUploadingFiles,
  } = useMutation({
    mutationKey: ['uploadFileUsingPresignedUrl'],
    mutationFn: async (params: { presignedUrl: string; file: File }) => {
      const response = await fetch(params.presignedUrl, {
        method: 'PUT',
        body: params.file,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to upload ${params.file.name}: ${response.statusText}`,
        );
      }
    },
  });

  const uploadFiles = async (files: File[]): Promise<string[]> => {
    try {
      // Generate presigned URLs for all files
      const { generatePresignedUrls: presignedUrls } =
        await generatePresignedUrlsMutate({
          generatePresignedUrlsInput: {
            files: files.map((file) => ({
              filename: file.name,
              contentType: file.type,
            })),
          },
        });

      // Upload each file to S3 using its presigned URL
      const uploadPromises = files.map(async (file, index) => {
        const presignedData = presignedUrls[index];

        await uploadFileUsingPresignedUrlMutate({
          presignedUrl: presignedData.uploadUrl,
          file,
        });

        return presignedData.publicUrl;
      });

      // Wait for all uploads to complete
      const publicUrls = await Promise.all(uploadPromises);
      return publicUrls;
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  return {
    uploadFiles,
    isPending: isGeneratingPresignedUrls || isUploadingFiles,
  };
};
